import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm, useWatch } from "react-hook-form";
import { useCreateRegistration } from "../../api/registrations";
import * as FormDialog from "../../shared/FormDialog";
import { CreatableTenantsSelect, useCreatableTenants } from "./CreatableTenantsSelect";
import type { ConnectedTenant } from "../../api/registrations.types";

import { trackEvent } from "../../shared/Snowplow";
import { useRegistrationVariant } from "./RegistrationVariantProvider";

type CreateRegistrationDialogProps = { onClose: () => void; onCreated: (id: string) => void };

type FormValues = { name: string; tenants: Array<ConnectedTenant> };

export const CreateRegistrationDialog: FC<CreateRegistrationDialogProps> = ({ onClose, onCreated }) => {
  const variant = useRegistrationVariant();
  const { t } = useTranslation();
  const { mutate: createRegistration, isError, isPending: isSubmitting } = useCreateRegistration();
  const { handleSubmit, reset, control, setValue, setFocus } = useForm<FormValues>({
    defaultValues: { name: "", tenants: [] },
  });

  const { onlyTenant, takesOneUnit } = useCreatableTenants();

  const withOnlyTenant = (tenants: Array<ConnectedTenant>) =>
    tenants.length === 0 && onlyTenant ? [onlyTenant] : tenants;

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleNewRegistration = ({ name, tenants: pickedTenants }: FormValues) => {
    const tenants = withOnlyTenant(pickedTenants);
    if (tenants.length === 0) throw new Error('"tenants" is required');
    trackEvent.createRegistrationClick();
    return createRegistration(
      { name, tenantIds: tenants.map(tenant => tenant.id) },
      {
        onSuccess: async ({ id }) => {
          onClose();
          reset();
          onCreated(id);
        },
      }
    );
  };

  const handleCancel = () => {
    onClose();
    reset();
  };

  const selectedTenants = withOnlyTenant(useWatch({ control, name: "tenants" }));

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateRegistration.Dialog.CreateTitle")}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handleNewRegistration)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("CreateRegistration.Dialog.RegistrationError") }}
              label={t("CreateRegistration.Dialog.NameLabel")}
              placeholder={t(`CreateRegistration.Dialog.${variant}.NamePlaceholder`)}
            />

            <FormDialog.ControlledField
              control={control}
              name="tenants"
              rules={{
                validate: tenants =>
                  withOnlyTenant(tenants).length > 0 ||
                  t(
                    takesOneUnit
                      ? "CreateRegistration.Dialog.TenantError.One"
                      : "CreateRegistration.Dialog.TenantError.Several"
                  ),
              }}
              label={t(
                takesOneUnit
                  ? "CreateRegistration.Dialog.TenantsLabel.One"
                  : "CreateRegistration.Dialog.TenantsLabel.Several"
              )}
            >
              {({ field: { name, onBlur, ref }, fieldState, id }) => (
                <CreatableTenantsSelect
                  id={id}
                  selectedTenants={selectedTenants}
                  onChange={tenants => setValue(name, tenants, { shouldValidate: true })}
                  error={Boolean(fieldState.error)}
                  inputRef={ref}
                  onBlur={onBlur}
                />
              )}
            </FormDialog.ControlledField>
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleCancel} />
          <FormDialog.SubmitButton isSubmitting={isSubmitting}>
            {t("CreateRegistration.Dialog.CreateSubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
